import React, { useState } from 'react'

import {
  Action,
  FieldSpec,
  LinkedField,
  PaginatedTable,
  RelatedLinkedField,
  ValueField,
  StatusField,
} from 'src/components/Table'
import {
  BooleanFilter,
  createCustomFieldFilter,
  createEnumFilter,
  createMultiQuerySelectFilter,
  DateTimeFilter,
  FilterHeader,
  MultStringFilter,
} from 'src/modules/shared/components/filters'
import { FreightShipment } from 'src/api/models'
import { FreightShipmentDialog } from 'src/modules/supply_chain/components/freight/ShipmentDialog'
import { skuit } from 'src/services/skuit'
import { ListTableProps } from './types'

const fieldOptions: { [key:string]: FieldSpec } = {
  trackingNumber: { id: 'trackingNumber', label: 'Tracking Number', ordering: 'tracking_number', renderer: LinkedField(ValueField) },
  client: { id: 'client', label: 'Client', ordering: 'account__name', renderer: RelatedLinkedField(ValueField, 'name') },
  order: { id: 'order', label: 'Order', ordering: 'order__number', renderer: RelatedLinkedField(ValueField, 'name') },
  consignee: { id: 'address.consignee', label: 'Consignee', renderer: ValueField },
  facility: { id: 'facility', label: 'Facility', ordering: 'facility__name', renderer: RelatedLinkedField(ValueField, 'name') },
  carrier: { id: 'carrier', label: 'Carrier', ordering: 'carrier__name', renderer: RelatedLinkedField(ValueField, 'name') },
  status: { id: 'status', label: 'Status', renderer: StatusField },
}

export const FreightShipmentListTable = (props: ListTableProps) => {
  const { paginator, fields, exportConfig, bulkActions, title = 'Freight Shipments' } = props
  const [selected, setSelected] = useState<FreightShipment[]>([])
  const [bulkEditOpen, setBulkEditOpen] = useState(false)

  const bulkOptions: { [key: string]: Action } = {
    bulkEdit: {
      label: 'Bulk Edit',
      action: () => setBulkEditOpen(true),
    },
  }

  return (
    <>
      {bulkEditOpen && (
        <FreightShipmentDialog
          open={bulkEditOpen}
          handleClose={() => setBulkEditOpen(false)}
          bulkUpdate
          shipments={selected}
        />
      )}
      <PaginatedTable
        title={title}
        fields={fields.map((field) => fieldOptions[field])}
        paginator={paginator}
        exportConfig={exportConfig}
        onSelectedItem={setSelected}
        bulkActions={bulkActions?.map((action) => bulkOptions[action])}
        filters={[
          {
            id: 'clients',
            label: 'Clients',
            widget: createMultiQuerySelectFilter(skuit.sc.clients.list),
          },
          {
            id: 'tracking_numbers',
            label: 'Tracking Numbers',
            widget: MultStringFilter,
          },
          {
            id: 'statuses',
            label: 'Status',
            widget: createEnumFilter(FreightShipment.Status),
          },
          {
            id: 'carriers',
            label: 'Carriers',
            widget: createMultiQuerySelectFilter(skuit.sc.carriers.list),
          },

          {
            id: 'scheduling_header',
            label: 'Scheduling',
            widget: FilterHeader,
          },
          {
            id: 'unscheduled',
            label: 'Unscheduled',
            widget: BooleanFilter,
          },
          {
            id: 'scheduled_on_start',
            label: 'Scheduled On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'scheduled_on_end',
            label: 'Scheduled On or Before',
            widget: DateTimeFilter,
          },

          {
            id: 'created_header',
            label: 'Created',
            widget: FilterHeader,
          },
          {
            id: 'created_on_start',
            label: 'Created On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'created_on_end',
            label: 'Created On or Before',
            widget: DateTimeFilter,
          },

          {
            id: 'orders_header',
            label: 'Order Filters',
            widget: FilterHeader,
          },
          {
            id: 'order_flags',
            label: 'Order Flags',
            widget: createMultiQuerySelectFilter(skuit.sc.freight.orders.flags.list),
          },
          {
            id: 'order_numbers',
            label: 'Order Numbers',
            widget: MultStringFilter,
          },
          {
            id: 'order_custom_fields',
            label: 'Order Custom Fields',
            widget: createCustomFieldFilter(
              skuit.sc.clients.list,
              skuit.sc.freight.orders.customFields.list
            ),
          },
        ]}
      />
    </>
  )
}
